import { useCallback, useMemo, useRef, useState } from "react";

import { List } from "../../../schema/list";

import {
	pickFreshestListData,
	readCampaignRevision,
	StoredCampaignEntry,
} from "./campaignRevision.util";

/** PATCH acknowledgements are readable before either the socket or query cache catches up. */
export function useAcknowledgedCampaign(
	socketList: List | undefined,
	cacheList: List | undefined,
	campaignUuid: string,
	onAcknowledged?: (entry: StoredCampaignEntry) => void
) {
	const listId = cacheList?.id ?? socketList?.id;
	const identity = `${listId}:${campaignUuid}`;
	const latestRef = useRef({ identity, onAcknowledged });

	latestRef.current = { identity, onAcknowledged };
	const [acknowledged, setAcknowledged] = useState<Record<string, StoredCampaignEntry>>({});
	const acknowledge = useCallback(
		(entry: StoredCampaignEntry) => {
			if (latestRef.current.identity !== identity) {
				return;
			}

			setAcknowledged((previous) =>
				previous[identity] &&
				readCampaignRevision(previous[identity]) >= readCampaignRevision(entry)
					? previous
					: { ...previous, [identity]: entry }
			);
			latestRef.current.onAcknowledged?.(entry);
		},
		[identity]
	);
	const listData = useMemo(() => {
		const fresh = pickFreshestListData(socketList, cacheList, campaignUuid);
		const cached = fresh?.campaignConfig?.[campaignUuid];
		const entry = acknowledged[identity];

		// A cache that no longer contains this campaign must still drive the deletion hand-off.
		if (!cached || !entry || readCampaignRevision(entry) <= readCampaignRevision(cached)) {
			return fresh;
		}

		return {
			...fresh,
			campaignConfig: { ...fresh?.campaignConfig, [campaignUuid]: entry },
		} as List;
	}, [socketList, cacheList, campaignUuid, identity, acknowledged]);

	return { listData, acknowledge };
}
